import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles/ContactForm.css";

export default function ContactForm({ prefill = {} }) {
  const { t } = useTranslation("contact");

  const packages = t("packages", { returnObjects: true });
  const services = t("services", { returnObjects: true });
  const urgencyOptions = t("urgencyOptions", { returnObjects: true });

  const [step, setStep] = useState(1);
  const [mode, setMode] = useState("pacote");
  const [formData, setFormData] = useState({
    nome: "", email: "", telemovel: "",
    mensagem: "", urgencia: "",
    pacote: "",
    services: [],
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (prefill.nome) setFormData((p) => ({ ...p, nome: prefill.nome }));
    if (prefill.mensagem) setFormData((p) => ({ ...p, mensagem: prefill.mensagem }));
  }, [prefill]);

  const progress = (step / 4) * 100;

  const update = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
    setErrors((e) => { const n = { ...e }; delete n[field]; return n; });
  };

  const toggleService = (svc) => {
    setFormData((p) => {
      const exists = p.services.includes(svc);
      return { ...p, services: exists ? p.services.filter((s) => s !== svc) : [...p.services, svc] };
    });
    setErrors((e) => { const n = { ...e }; delete n.services; return n; });
  };

  const switchMode = (m) => {
    setMode(m);
    setFormData((p) => ({ ...p, pacote: "", services: [] }));
    setErrors({});
  };

  const validateStep = () => {
    const e = {};
    if (step === 1) {
      if (mode === "pacote" && !formData.pacote) e.selection = t("errors.selectPackage");
      if (mode === "servico" && formData.services.length === 0) e.selection = t("errors.selectService");
    }
    if (step === 2) {
      if (!formData.mensagem.trim()) e.mensagem = t("errors.messageRequired");
      else if (formData.mensagem.trim().length < 10) e.mensagem = t("errors.messageTooShort");
      if (!formData.urgencia) e.urgencia = t("errors.urgencyRequired");
    }
    if (step === 3) {
      if (!formData.nome.trim()) e.nome = t("errors.nameRequired");
      if (!formData.email.trim()) e.email = t("errors.emailRequired");
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = t("errors.emailInvalid");
      if (!formData.telemovel.trim()) e.telemovel = t("errors.phoneRequired");
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const nextStep = () => { if (validateStep()) setStep((p) => Math.min(p + 1, 4)); };
  const prevStep = () => setStep((p) => Math.max(p - 1, 1));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step !== 4) return;
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        selectionMode: mode,
        selectionLabel: mode === "pacote"
          ? packages.find((p) => p.id === formData.pacote)?.name
          : formData.services.join(", "),
      };
      const res = await fetch("/send-form.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Error sending.");
      setIsSuccess(true);
      setFormData({ nome: "", email: "", telemovel: "", mensagem: "", urgencia: "", pacote: "", services: [] });
      setStep(1);
      setErrors({});
    } catch (err) {
      alert(err.message || "An error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectionLabel = mode === "pacote"
    ? packages.find((p) => p.id === formData.pacote)?.name
    : formData.services.join(", ");

  return (
    <section className="cf-wrapper">
      <form className="cf-form" onSubmit={handleSubmit}>

        <div className="cf-header">
          <span className="cf-eyebrow">{t("eyebrow")}</span>
          <h2 className="cf-title">{t("title")}</h2>
          <p className="cf-subtitle">{t("subtitle")}</p>
          <div className="cf-progress">
            <div className="cf-progress__fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="cf-step-label">{t("stepLabel", { step })}</span>
        </div>

        {isSuccess && (
          <div className="cf-success">{t("success")}</div>
        )}

        {/* Step 1 */}
        {step === 1 && (
          <div className="cf-step">
            <h3 className="cf-step__title">{t("step1.title")}</h3>

            <div className="cf-mode-toggle">
              <button
                type="button"
                className={`cf-mode-btn${mode === "pacote" ? " cf-mode-btn--active" : ""}`}
                onClick={() => switchMode("pacote")}
              >
                {t("step1.modePackage")}
              </button>
              <button
                type="button"
                className={`cf-mode-btn${mode === "servico" ? " cf-mode-btn--active" : ""}`}
                onClick={() => switchMode("servico")}
              >
                {t("step1.modeService")}
              </button>
            </div>

            {mode === "pacote" && (
              <div className="cf-packages">
                {packages.map((pkg) => (
                  <button
                    key={pkg.id}
                    type="button"
                    className={`cf-pkg${formData.pacote === pkg.id ? " cf-pkg--active" : ""}${pkg.featured ? " cf-pkg--featured" : ""}${pkg.id === "custom" ? " cf-pkg--custom" : ""}`}
                    onClick={() => update("pacote", pkg.id)}
                  >
                    {pkg.featured && <span className="cf-pkg__badge">{t("featuredBadge")}</span>}
                    <span className="cf-pkg__name">{pkg.name}</span>
                    <span className="cf-pkg__tagline">{pkg.tagline}</span>
                    {pkg.features.length > 0 && (
                      <ul className="cf-pkg__features">
                        {pkg.features.map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    )}
                  </button>
                ))}
              </div>
            )}

            {mode === "servico" && (
              <div className="cf-services">
                {services.map((svc) => (
                  <button
                    key={svc}
                    type="button"
                    className={`cf-svc-pill${formData.services.includes(svc) ? " cf-svc-pill--active" : ""}`}
                    onClick={() => toggleService(svc)}
                  >
                    {formData.services.includes(svc) && <span className="cf-svc-check">✓</span>}
                    {svc}
                  </button>
                ))}
              </div>
            )}

            {errors.selection && <span className="cf-error">{errors.selection}</span>}
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="cf-step">
            <h3 className="cf-step__title">{t("step2.title")}</h3>
            <div className="cf-field">
              <label>{t("step2.messageLabel")}</label>
              <textarea
                placeholder={t("step2.messagePlaceholder")}
                value={formData.mensagem}
                onChange={(e) => update("mensagem", e.target.value)}
              />
              {errors.mensagem && <span className="cf-error">{errors.mensagem}</span>}
            </div>
            <div className="cf-field">
              <label>{t("step2.urgencyLabel")}</label>
              <div className="cf-pills">
                {urgencyOptions.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`cf-pill${formData.urgencia === opt ? " cf-pill--active" : ""}`}
                    onClick={() => update("urgencia", opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {errors.urgencia && <span className="cf-error">{errors.urgencia}</span>}
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="cf-step">
            <h3 className="cf-step__title">{t("step3.title")}</h3>
            <div className="cf-field">
              <label>{t("step4.labelName")}</label>
              <input type="text" placeholder={t("step3.namePlaceholder")} value={formData.nome} onChange={(e) => update("nome", e.target.value)} />
              {errors.nome && <span className="cf-error">{errors.nome}</span>}
            </div>
            <div className="cf-field">
              <label>{t("step4.labelEmail")}</label>
              <input type="email" placeholder={t("step3.emailPlaceholder")} value={formData.email} onChange={(e) => update("email", e.target.value)} />
              {errors.email && <span className="cf-error">{errors.email}</span>}
            </div>
            <div className="cf-field">
              <label>{t("step4.labelPhone")}</label>
              <input type="tel" placeholder={t("step3.phonePlaceholder")} value={formData.telemovel} onChange={(e) => update("telemovel", e.target.value)} />
              {errors.telemovel && <span className="cf-error">{errors.telemovel}</span>}
            </div>
          </div>
        )}

        {/* Step 4 */}
        {step === 4 && (
          <div className="cf-step">
            <h3 className="cf-step__title">{t("step4.title")}</h3>
            <div className="cf-review">
              <div className="cf-review__item">
                <span>{mode === "pacote" ? t("step4.labelPackage") : t("step4.labelServices")}</span>
                <strong>{selectionLabel}</strong>
              </div>
              <div className="cf-review__item">
                <span>{t("step4.labelMessage")}</span>
                <strong>{formData.mensagem}</strong>
              </div>
              <div className="cf-review__item">
                <span>{t("step4.labelUrgency")}</span>
                <strong>{formData.urgencia}</strong>
              </div>
              <div className="cf-review__item">
                <span>{t("step4.labelName")}</span>
                <strong>{formData.nome}</strong>
              </div>
              <div className="cf-review__item">
                <span>{t("step4.labelEmail")}</span>
                <strong>{formData.email}</strong>
              </div>
              <div className="cf-review__item">
                <span>{t("step4.labelPhone")}</span>
                <strong>{formData.telemovel}</strong>
              </div>
            </div>
          </div>
        )}

        <div className="cf-actions">
          {step > 1 && (
            <button type="button" className="cf-btn cf-btn--ghost" onClick={prevStep}>
              <span>{t("actions.back")}</span>
            </button>
          )}
          {step < 4 ? (
            <button type="button" className="cf-btn cf-btn--primary" onClick={nextStep}>
              <span>{t("actions.next")}</span>
            </button>
          ) : (
            <button type="submit" className="cf-btn cf-btn--primary" disabled={isSubmitting}>
              <span>{isSubmitting ? t("actions.sending") : t("actions.submit")}</span>
            </button>
          )}
        </div>

      </form>
    </section>
  );
}
